import { paymentReceiptsService, type PaymentReceipt } from '@/services';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  INVALID_RECEIPT_LINK_VIEW,
  RECEIPT_LOAD_ERROR_VIEW,
  buildPaymentReceiptView,
  type PaymentReceiptView,
} from '../utils/payment-receipt-view.util';

/** Enquanto o pagamento é confirmado, a página relê o comprovante a cada 5 s. */
const POLL_INTERVAL_MS = 5_000;
/** Cerca de 2 minutos: depois disso para de consultar e muda o texto. */
const MAX_POLLS = 24;

type ReceiptResult =
  | { kind: 'found'; receipt: PaymentReceipt }
  | { kind: 'not-found' };

const isProcessing = (data?: ReceiptResult): boolean =>
  data?.kind === 'found' && data.receipt.status === 'PROCESSING';

/**
 * Comprovante da página pública. Com o pagamento ainda sem confirmação (o
 * segurado costuma voltar da Autra antes do aviso dela), repete a leitura até
 * ele mudar ou o tempo de espera acabar.
 */
export function usePaymentReceipt(token: string) {
  const queryClient = useQueryClient();
  const queryKey = ['payment-receipt', token];

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<ReceiptResult> => {
      const response = await paymentReceiptsService.getReceipt(token);
      if (response.notFound) return { kind: 'not-found' };
      if (!response.success || !response.data) {
        throw new Error(
          response.error || 'Não foi possível carregar o comprovante.'
        );
      }
      return { kind: 'found', receipt: response.data };
    },
    enabled: Boolean(token),
    retry: 2,
    refetchOnWindowFocus: false,
    refetchInterval: current =>
      isProcessing(current.state.data) &&
      current.state.dataUpdateCount < MAX_POLLS
        ? POLL_INTERVAL_MS
        : false,
  });

  // Cada leitura re-renderiza a página, então o contador está sempre em dia aqui.
  const waitingTooLong =
    isProcessing(query.data) &&
    (queryClient.getQueryState(queryKey)?.dataUpdateCount ?? 0) >= MAX_POLLS;

  return {
    view: toView(query.data, query.isError, waitingTooLong),
    brokerName:
      query.data?.kind === 'found' ? query.data.receipt.brokerName : undefined,
    /** Só a parcela paga tem comprovante em PDF. */
    isPaid:
      query.data?.kind === 'found' && query.data.receipt.status === 'PAID',
    isLoading: query.isPending && !query.isError,
    isRefreshing: isProcessing(query.data) && !waitingTooLong,
    canRetry: query.isError && !query.data,
    retry: () => void query.refetch(),
  };
}

function toView(
  data: ReceiptResult | undefined,
  isError: boolean,
  waitingTooLong: boolean
): PaymentReceiptView | undefined {
  if (data?.kind === 'not-found') return INVALID_RECEIPT_LINK_VIEW;
  if (data?.kind === 'found') {
    return buildPaymentReceiptView(data.receipt, { waitingTooLong });
  }
  return isError ? RECEIPT_LOAD_ERROR_VIEW : undefined;
}
